// Regras puras da tela de Desafios (admin): sem React, sem rede.
import moment from 'moment';
import {
  TIPO_JOGO,
  type DailyChallengeItem,
  type DailyChallengeItemInput,
  type PickableGame,
} from '../../services/admin';
import { gameKey } from './TorneioGamePicker';
import { moveItem } from './move-item';

export { gameKey };

export const MAX_DESAFIO_GAMES = 5;
export const RECOMMENDED_DESAFIO_GAMES = 3;

const DAY_FORMAT = 'YYYY-MM-DD';

export const monthRange = (month: moment.Moment): { from: string; to: string } => ({
  from: month.clone().startOf('month').format(DAY_FORMAT),
  to: month.clone().endOf('month').format(DAY_FORMAT),
});

/** A API pode mandar ISO UTC; `moment(iso)` em UTC−3 viraria o dia anterior. */
export const challengeDay = (date: string): string => date.slice(0, 10);

export const formatDay = (day: string): string => moment(day, DAY_FORMAT).format('DD/MM/YYYY');

type Labelled = { kind: 'passatempo' | 'jogo'; tipoId: number | null };

const JOGO_LABEL: Record<number, string> = {
  [TIPO_JOGO.REBUS]: 'Rébus',
  [TIPO_JOGO.QUIZ]: 'Quiz',
  [TIPO_JOGO.CACA_PALAVRAS]: 'Caça-palavras',
  [TIPO_JOGO.SUDOKU]: 'Sudoku',
};

export const tipoLabel = (g: Labelled): string => {
  if (g.kind === 'passatempo') return 'Cruzada';
  return (g.tipoId !== null && JOGO_LABEL[g.tipoId]) || 'Jogo';
};

export const gameLine = (position: number, g: Labelled & { titulo: string | null }): string =>
  `${position}. ${tipoLabel(g)} — ${g.titulo ?? 'Sem título'}`;

export const fromChallengeItems = (items: DailyChallengeItem[]): PickableGame[] =>
  [...items]
    .sort((a, b) => a.position - b.position)
    .map((i) => ({
      kind: i.kind,
      id: i.id,
      titulo: i.titulo,
      subtitulo: null,
      tipoId: i.tipoId,
      edicaoId: null,
      edicaoNome: null,
    }));

export const toItemInputs = (games: PickableGame[]): DailyChallengeItemInput[] =>
  games.map((g) => (g.kind === 'passatempo' ? { passatemposId: g.id } : { jogosId: g.id }));

/** O picker não ordena: a ordem do desafio é do pai. Novos entram no fim. */
export const applyPickerChange = (
  prev: PickableGame[],
  next: PickableGame[],
  max = MAX_DESAFIO_GAMES,
): { games: PickableGame[]; overflow: boolean } => {
  const nextKeys = new Set(next.map(gameKey));
  const prevKeys = new Set(prev.map(gameKey));
  const games = [
    ...prev.filter((g) => nextKeys.has(gameKey(g))),
    ...next.filter((g) => !prevKeys.has(gameKey(g))),
  ];
  return games.length > max ? { games: prev, overflow: true } : { games, overflow: false };
};

export const moveGame = (games: PickableGame[], index: number, delta: -1 | 1): PickableGame[] =>
  moveItem(games, index, delta);

export const isDisabledDay = (
  day: moment.Moment,
  today: moment.Moment,
  mode: 'create' | 'edit',
): boolean => (mode === 'create' ? day.isBefore(today, 'day') : day.isSameOrBefore(today, 'day'));

export const apiErrorMessage = (err: unknown): string | undefined => {
  const message = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message;
  if (Array.isArray(message)) return message.join('; ') || undefined;
  return typeof message === 'string' ? message : undefined;
};
